import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const MANAGER_REPORT_TIMEOUT_MS = 3000;

export type ConsentBannerContextType = {
  isConsentResolved: boolean;
  setIsBannerOpen(open: boolean): void;
};

const ConsentBannerContext = createContext<ConsentBannerContextType>({
  isConsentResolved: true,
  setIsBannerOpen: () => undefined,
});

export const ConsentBannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const requiresConsent = Boolean(process.env.NEXT_PUBLIC_ANALYTICS_WRITE_KEY);
  const [isBannerOpen, setIsBannerOpen] = useState<boolean | null>(null);
  const [managerReportTimedOut, setManagerReportTimedOut] = useState(false);

  useEffect(() => {
    if (!requiresConsent) return;

    const timeout = setTimeout(() => setManagerReportTimedOut(true), MANAGER_REPORT_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [requiresConsent]);

  const contextValue = useMemo(() => {
    const managerReported = isBannerOpen !== null;
    const isConsentResolved = !requiresConsent || (managerReported ? !isBannerOpen : managerReportTimedOut);

    return { isConsentResolved, setIsBannerOpen };
  }, [requiresConsent, isBannerOpen, managerReportTimedOut]);

  return <ConsentBannerContext.Provider value={contextValue}>{children}</ConsentBannerContext.Provider>;
};

export const useConsentBanner = (): ConsentBannerContextType => useContext(ConsentBannerContext);
